import { z } from "zod";
import { Alert, Button, Group, Modal, PasswordInput, Select, Stack, Switch, TextInput } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useTRPC } from "../../../trpc";
import { nameSchema, passwordMismatch, passwordSchemaStrength } from "../../../utils/schema";
import { useForm } from "@mantine/form";
import { PasswordStrength } from "../../../components/PasswordStrength";
import { IconExclamationCircle } from "@tabler/icons-react";
import { supportedLocalesOptions } from "@project/common";
import type { FC, ReactNode } from "react";
import type { Locale } from "@project/common";
import { useMapkaNotification } from "../../../hooks/notifications.js";

interface AddAccountModalProps {
  opened: boolean;
  onClose: () => void;
}

const defaultFormValues = {
  email: "",
  name: "",
  password: "",
  organization: "",
  passwordConfirmation: "",
  marketingConsent: false,
  isDevelopmentPartner: false,
  locale: "en" as Locale,
};

export const AddAccountModal: FC<AddAccountModalProps> = ({ opened, onClose }) => {
  const intl = useIntl();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending, error } = useMutation(trpc.createUserAccount.mutationOptions());
  const mapkaNotification = useMapkaNotification();

  const validate = useMemo(() => zod4Resolver(
    z.object({
      email: z.email(),
      name: nameSchema(intl),
      password: passwordSchemaStrength(intl),
      passwordConfirmation: passwordSchemaStrength(intl),
      locale: z.string(),
      isDevelopmentPartner: z.boolean(),
      marketingConsent: z.boolean(),
    }).refine(data => data.password === data.passwordConfirmation, {
      message: passwordMismatch(intl),
      path: ["passwordConfirmation"],
    }),
  ), [intl]);

  const { getInputProps, onSubmit, isValid, reset, values } = useForm({
    validateInputOnBlur: true,
    initialValues: defaultFormValues,
    validate,
  });

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const handleSubmit = useCallback((form: typeof values) => {
    void mutateAsync(form)
      .then(() => {
        mapkaNotification({
          variant: "success",
          title: intl.formatMessage({
            defaultMessage: "Account Created",
            id: "notification.accountCreated",
          }),
          message: intl.formatMessage({
            defaultMessage: "Account {name} has been created successfully",
            id: "notification.accountCreatedMessage",
          },
          { name: form.name },
          ),
        });
        void queryClient
          .invalidateQueries(trpc.getAccounts.queryFilter())
          .then(() => {
            handleClose();
          });
      });
  }, [mutateAsync, handleClose]);

  let errorMessage: ReactNode = null;
  if (error) {
    if (error.message.includes("Email is disposable")) {
      errorMessage = (
        <FormattedMessage
          id="error.disposableEmail"
          defaultMessage="Disposable email addresses are not allowed"
        />
      );
    }
    else {
      errorMessage = error.message;
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} size="xl" title={intl.formatMessage({ defaultMessage: "Create new account", id: "title.createNewAccount" })}>
      <form onSubmit={onSubmit(handleSubmit)}>
        <Stack gap="xs">
          {error && (
            <Alert variant="light" color="red" icon={<IconExclamationCircle />}>
              {errorMessage}
            </Alert>
          )}
          <Group>
            <TextInput
              required
              flex={1}
              autoComplete="email"
              label={<FormattedMessage id="label.adminEmail" defaultMessage="Admin email" />}
              {...getInputProps("email")}
            />
            <TextInput
              required
              flex={1}
              autoComplete="name"
              label={<FormattedMessage id="label.adminFullName" defaultMessage="Admin full name" />}
              {...getInputProps("name")}
            />
          </Group>

          <TextInput
            required
            autoComplete="organization"
            label={<FormattedMessage id="label.organization" defaultMessage="Organization" />}
            {...getInputProps("organization")}
          />
          <Select
            label={<FormattedMessage id="label.locale" defaultMessage="Locale" />}
            data={supportedLocalesOptions.map(({ value, localLabel }) => ({ value, label: localLabel }))}
            allowDeselect={false}
            searchable={true}
            {...getInputProps("locale")}
          />

          <Switch
            py="xs"
            label={<FormattedMessage id="label.isDevelopmentPartner" defaultMessage="Is development partner" />}
            {...getInputProps("isDevelopmentPartner", { type: "checkbox" })}
          />

          <Group>
            <PasswordInput
              required
              flex={1}
              autoComplete="new-password"
              label={<FormattedMessage id="label.password" defaultMessage="Password" />}
              {...getInputProps("password")}
            />
            <PasswordInput
              required
              flex={1}
              autoComplete="new-password"
              label={<FormattedMessage id="label.passwordConfirmation" defaultMessage="Password confirmation" />}
              {...getInputProps("passwordConfirmation")}
            />
          </Group>
          <PasswordStrength {...values} />

          <Group gap="xs" mt="xs" justify="flex-end">
            <Button variant="secondary" type="reset" onClick={onClose}>
              <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
            </Button>
            <Button variant="tertiary" type="submit" loading={isPending} disabled={!isValid()}>
              <FormattedMessage id="button.create" defaultMessage="Create" />
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
